import React from "react";
import { SEO } from "./SEO";

// Define interface for product attribute values
interface ProductAttributeValue {
  name: string;
  colorHex?: string | null;
}

// Define interface for product attributes
interface ProductAttribute {
  name: string;
  uniqueName: string;
  isGroupingAttribute?: boolean;
  groupingAttributeStyle?: string | null;
  values?: ProductAttributeValue[];
}

interface ProductSEOProps {
  product: any; // Update to use the specific product type from your API
}

export const ProductSEO: React.FC<ProductSEOProps> = ({ product }) => {
  // Add validation to ensure we have a valid product
  if (!product || !product.name) {
    console.error("ProductSEO received invalid product data:", product);
    return null;
  }

  console.log("Rendering ProductSEO for:", product.name);

  // Log SEO data to verify it's properly structured
  if (product.seo) {
    console.log("SEO data:", {
      metaTitle: product.seo.metaTitle,
      metaDescription: product.seo.metaDescription,
      hasOpenGraph: !!product.seo.openGraph,
      openGraphTitle: product.seo.openGraph?.["og:title"],
      openGraphDescription: product.seo.openGraph?.["og:description"],
    });
  }

  // Create structured data for Product schema with fallbacks for missing data
  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name,
    description:
      product.description || `${product.name} - Disponible en Evefant`,
    image: product.images?.[0]?.medium || product.images?.[0]?.small || "",
    sku: product.sku || "",
    brand: {
      "@type": "Brand",
      name: product.partner?.name || "Evefant",
    },
    offers: {
      "@type": "Offer",
      url:
        product.seo?.canonicalURL ||
        (typeof window !== "undefined" ? window.location.href : ""),
      priceCurrency: "PEN",
      price: product.price?.discount?.newPrice || product.price?.regPrice || 0,
      priceValidUntil:
        product.price?.discount?.endDatetime ||
        (() => {
          // Always set a future date (1 year from now) if not present
          const d = new Date();
          d.setFullYear(d.getFullYear() + 1);
          return d.toISOString().split("T")[0];
        })(),
      availability:
        product.stock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      seller: {
        "@type": "Organization",
        name: product.partner?.name || "Evefant",
      },
    },
    // Add author information
    author: {
      "@type": "Organization",
      name: product.partner?.name || "Evefant",
      url: product.partner?.website || "https://evefant.com",
    },
    // Add publisher information
    publisher: {
      "@type": "Organization",
      name: "Evefant",
      logo: {
        "@type": "ImageObject",
        url: "https://evefant.com/logo.png",
      },
      url: "https://evefant.com",
    },
    // Add return policy for Google Product snippets
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy",
      url: "https://evefant.com/legales/politica-cambios-devoluciones",
    },
  };

  // Extract attributes for meta keywords - with proper type annotations
  const attributeValues =
    product.attributes
      ?.flatMap(
        (attr: ProductAttribute) =>
          attr.values?.map((val: ProductAttributeValue) => val.name) || []
      )
      .filter(Boolean) || [];

  // Combine product name with attributes for keywords
  const keywords = [product.name, ...attributeValues, product.partner?.name]
    .filter(Boolean)
    .join(", ");

  // Use SEO data from API if available, otherwise generate from product data
  const seoTitle = product.seo?.metaTitle || product.name;
  const seoDescription =
    product.seo?.metaDescription ||
    product.description?.substring(0, 160) ||
    `Compra ${product.name} en Evefant`;
  const seoKeywords = product.seo?.keywords || keywords;
  const canonicalURL =
    product.seo?.canonicalURL || `https://evefant.com/p/${product.url}`;

  // Extract OpenGraph data if available
  const openGraph = product.seo?.openGraph || null;

  // Get image URL from multiple possible sources with fallbacks
  const imageUrl =
    // First try OpenGraph image
    openGraph?.["og:image"]?.url ||
    // Then try meta image
    product.seo?.metaImage?.url ||
    // Then try product images
    product.images?.[0]?.medium ||
    product.images?.[0]?.small ||
    "";

  return (
    <SEO
      title={seoTitle}
      description={seoDescription}
      image={imageUrl}
      type={openGraph?.["og:type"] || "product"}
      schema={productSchema}
      openGraph={openGraph} // Pass OpenGraph data to SEO component
      seoData={product.seo} // Pass entire SEO object
      // Add author and publisher as props
      author={product.partner?.name || "Evefant"}
      publisher="Evefant"
    >
      {/* Additional meta tags for SEO */}
      <meta name="keywords" content={seoKeywords} />

      {/* Include author and publisher meta tags */}
      <meta name="author" content={product.partner?.name || "Evefant"} />
      <meta name="publisher" content="Evefant" />

      {/* Include metaRobots if available */}
      {product.seo?.metaRobots && (
        <meta name="robots" content={product.seo.metaRobots} />
      )}

      {/* Include metaViewport if available */}
      {product.seo?.metaViewport && (
        <meta name="viewport" content={product.seo.metaViewport} />
      )}

      <meta
        property="product:price:amount"
        content={String(product.price?.regPrice)}
      />
      <meta property="product:price:currency" content="PEN" />

      {/* Add discount meta tags if product is on sale */}
      {product.price?.discount && (
        <>
          <meta
            property="product:sale_price:amount"
            content={String(product.price.discount.newPrice)}
          />
          <meta property="product:sale_price:currency" content="PEN" />
        </>
      )}

      {/* Availability */}
      <meta
        property="product:availability"
        content={product.stock > 0 ? "in stock" : "out of stock"}
      />
      <meta property="product:retailer_item_id" content={product.sku} />
      <meta property="product:brand" content={product.partner?.name || ""} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalURL} />
    </SEO>
  );
};

export default ProductSEO;
